import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

export default function AdvisorSignup() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const userData = await register(email, password, name);
      toast.success('Account created — welcome to SteelPeak!');
      if (userData?.role === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/advisor/dashboard');
      }
    } catch (e) {
      const errorMsg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 36, 68, 0.8), rgba(30, 58, 99, 0.8)), url('https://images.pexels.com/photos/5816285/pexels-photo-5816285.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`
        }}
      >
        <div className="absolute inset-0 flex flex-col justify-center items-center text-white p-12">
          <h1 className="text-5xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '0.05em' }}>
            <span style={{ fontWeight: 800 }}>STEELPEAK</span>{' '}
            <span style={{ fontWeight: 300 }}>WEALTH</span>
          </h1>
          <p className="text-xl text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Trusted wealth management for your future
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-[#f8fafc]">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-8">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-[#0f172a] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="signup-heading">
                Create your account
              </h2>
              <p className="text-[#475569]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Your email must be pre-approved by an administrator
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md" data-testid="signup-error">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name" className="text-[#0f172a]">Full Name</Label>
                <Input
                  id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} required
                  placeholder="Jane Doe" autoComplete="name"
                  className="mt-2" data-testid="signup-name-input"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-[#0f172a]">Email</Label>
                <Input
                  id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  placeholder="you@spwm.com" autoComplete="email"
                  className="mt-2" data-testid="signup-email-input"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-[#0f172a]">Password</Label>
                <Input
                  id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                  placeholder="At least 8 characters" autoComplete="new-password"
                  className="mt-2" data-testid="signup-password-input"
                />
              </div>
              <div>
                <Label htmlFor="confirm-password" className="text-[#0f172a]">Confirm Password</Label>
                <Input
                  id="confirm-password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required
                  placeholder="Re-enter password" autoComplete="new-password"
                  className="mt-2" data-testid="signup-confirm-password-input"
                />
              </div>

              <Button
                type="submit" disabled={loading}
                className="w-full bg-gradient-to-r from-[#0f2444] via-[#1e3a63] to-[#26518c] hover:opacity-90 text-white font-medium py-2.5 transition-all hover:shadow-md"
                data-testid="signup-submit-button"
              >
                {loading ? 'Creating account...' : 'Create Account'}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-[#475569]">
              Already have an account?{' '}
              <Link to="/advisor/login" className="text-[#5b93d6] hover:underline" data-testid="back-to-login-link">
                Sign in
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
